import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const SESSION_KEY = "econation_chat_session";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = "sess_" + crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

const WELCOME = "Bonjour ! Je suis Léo, votre assistant ECONATION. Posez-moi vos questions sur les CEE, l'audit énergétique ou le financement de vos travaux — je suis là pour vous orienter.";

const suggestions = [
  "Suis-je éligible aux CEE ?",
  "Combien rapporte une pompe à chaleur ?",
  "Comment se passe l'audit ?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(typeof window !== "undefined" ? getSessionId() : "");

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Show "unread" pulse 8 seconds after first load
  useEffect(() => {
    const t = setTimeout(() => !open && setUnread(true), 8000);
    return () => clearTimeout(t);
  }, [open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setMessages((m) => [...m, { role: "user", content }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: content }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, je rencontre un souci technique. Vous pouvez nous appeler au 01 86 90 14 48 ou utiliser le formulaire de contact." }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => { setOpen(true); setUnread(false); }}
          aria-label="Ouvrir le chat ECONATION"
          className="fixed bottom-6 right-6 z-[60] group"
        >
          <div className="relative h-14 w-14 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] grid place-items-center shadow-[0_18px_50px_-12px_rgba(16,185,129,0.7)] hover:scale-110 transition-transform">
            <MessageCircle className="h-6 w-6 text-white" strokeWidth={2} />
            {unread && (
              <>
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#0055A4] grid place-items-center text-[10px] font-bold text-white">1</span>
                <span className="absolute inset-0 rounded-full bg-[#10B981] animate-ping opacity-40" />
              </>
            )}
          </div>
          <span className="absolute right-[68px] top-1/2 -translate-y-1/2 whitespace-nowrap bg-white text-[#0F172A] text-sm font-medium px-3.5 py-2 rounded-full shadow-[0_8px_24px_-8px_rgba(0,0,0,0.3)] opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all pointer-events-none">
            Une question ?
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-[60] w-[min(92vw,400px)] h-[min(80vh,640px)] bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(15,23,42,0.4)] border border-black/[0.06] flex flex-col overflow-hidden"
        >
          {/* header */}
          <div className="relative bg-gradient-to-br from-[#0A0F1A] via-[#064E3B] to-[#0A0F1A] p-5 text-white">
            <div className="absolute inset-0 bg-grid-soft opacity-[0.07] pointer-events-none" />
            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="relative h-11 w-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 grid place-items-center">
                  <Sparkles className="h-5 w-5 text-[#10B981]" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#10B981] border-2 border-[#0A0F1A]" />
                </div>
                <div className="leading-tight">
                  <div className="font-display font-semibold">Léo · Expert CEE</div>
                  <div className="text-[11px] text-white/65 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" />
                    En ligne · ECONATION
                  </div>
                </div>
              </div>
              <button
                data-testid="chat-close"
                onClick={() => setOpen(false)}
                aria-label="Fermer le chat"
                className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* messages */}
          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-5 space-y-3 bg-gradient-to-b from-[#F8FAFC] to-white">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[82%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0F172A] text-white rounded-2xl rounded-br-md"
                      : "bg-white border border-black/[0.06] text-[#0F172A] rounded-2xl rounded-bl-md shadow-[0_4px_18px_-10px_rgba(15,23,42,0.15)]"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-black/[0.06] rounded-2xl rounded-bl-md px-4 py-3 shadow-[0_4px_18px_-10px_rgba(15,23,42,0.15)]">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-[#10B981] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="h-2 w-2 rounded-full bg-[#10B981] animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="h-2 w-2 rounded-full bg-[#10B981] animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="pt-3 flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    data-testid={`chat-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => send(s)}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#10B981]/30 text-[#064E3B] hover:bg-[#10B981] hover:text-white hover:border-[#10B981] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* input */}
          <div className="border-t border-black/[0.06] p-3 bg-white">
            <div className="flex items-end gap-2 bg-[#F8FAFC] rounded-2xl border border-black/[0.06] p-2 focus-within:border-[#10B981]/50 transition-colors">
              <textarea
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Écrivez votre question…"
                disabled={loading}
                className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm placeholder-[#94A3B8] text-[#0F172A] outline-none max-h-32"
                style={{ minHeight: "32px" }}
              />
              <button
                data-testid="chat-send"
                onClick={() => send()}
                disabled={loading || !input.trim()}
                aria-label="Envoyer"
                className="h-9 w-9 grid place-items-center rounded-xl bg-[#10B981] hover:bg-[#059669] disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors shrink-0"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            <div className="text-[10px] text-[#94A3B8] text-center mt-2">
              Assistant IA — peut faire des erreurs. Vérifiez les infos clés.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
